import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Check } from 'lucide-react';
import { Button, cn } from '@bliks-io/ui';
import { Header } from '@/layout/header/Header';
import { WizardRail } from './WizardRail';
import { AutosaveIndicator } from './AutosaveIndicator';
import type { AutosaveStatus } from '../autosave/useWizardAutosave';
import type { WizardProgress, WizardStepId } from '../wizardSteps';

interface WizardShellProps {
  currentStep: WizardStepId;
  progress: WizardProgress;
  /** Optional steps the user skipped this session — shown as acknowledged. */
  skippedSteps: Set<WizardStepId>;
  onStepClick: (stepId: WizardStepId) => void;
  onBack?: () => void;
  onNext: () => void;
  onSkip?: () => void;
  nextLabel: string;
  /** Abschließen renders a leading check instead of the trailing arrow */
  nextIcon?: 'arrow' | 'check';
  /** Intro screens render full-width on white, without the step rail. */
  isIntro?: boolean;
  isSaving?: boolean;
  /** Blocks Weiter on the non-skippable anchor steps while their table is empty. */
  nextDisabled?: boolean;
  /** Silent-autosave readout in the footer — rendered only when provided. */
  autosaveStatus?: AutosaveStatus;
  children: ReactNode;
}

export function WizardShell({
  currentStep,
  progress,
  skippedSteps,
  onStepClick,
  onBack,
  onNext,
  onSkip,
  nextLabel,
  nextIcon = 'arrow',
  isIntro = false,
  isSaving = false,
  nextDisabled = false,
  autosaveStatus,
  children
}: WizardShellProps) {
  const { t } = useTranslation();

  return (
    // h-screen: the wizard renders outside the AppLayout chrome (the org is
    // unusable until Abschließen), so it owns the full viewport itself.
    <div className="flex h-screen flex-col">
      <Header />

      <div className="flex min-h-0 flex-1">
        {!isIntro && (
          <WizardRail
            currentStep={currentStep}
            progress={progress}
            skippedSteps={skippedSteps}
            onStepClick={onStepClick}
          />
        )}

        <div
          className={cn('flex min-w-0 flex-1 flex-col', isIntro ? 'bg-white' : 'bg-surface-subtle')}
        >
          <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>

          <div className="flex items-center justify-between border-t border-line bg-white px-10 py-4">
            <div>
              {onBack && (
                <Button variant="outline" size="medium" onClick={onBack} disabled={isSaving}>
                  {t('common.back')}
                </Button>
              )}
            </div>
            <div className="flex items-center gap-3.5">
              {autosaveStatus && <AutosaveIndicator status={autosaveStatus} />}
              {onSkip && (
                <Button variant="ghost" size="medium" onClick={onSkip} disabled={isSaving}>
                  {t('initialSetup.footer.skip')}
                </Button>
              )}
              <Button
                variant="brand"
                size="medium"
                onClick={onNext}
                loading={isSaving}
                disabled={isSaving || nextDisabled}
              >
                {nextIcon === 'check' && <Check className="size-4!" strokeWidth={2.5} />}
                {nextLabel}
                {nextIcon === 'arrow' && <ArrowRight className="size-4!" />}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
